import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useInventoryRuns, useScanAsset, useCompleteRun } from '@/hooks/useFaInventory';
import type { InventoryRun, ScanResult } from '@/services/fa-inventory-api';
import { isNativeScanAvailable, scanOnce } from '@/services/native-scan';
import '@/styles/fa-inventory.css';

interface HistoryEntry extends ScanResult {
  id: number;
  at: string;
}

/**
 * Single-input scan pattern (per team convention): one field that accepts
 * scans in sequence. Submit on Enter, auto-clear, auto-refocus, vibrate on
 * success/error. Handheld scanners just type-enter — works out of the box.
 */
export function FaInventoryRoute() {
  const { t } = useTranslation();
  const { data: runs = [], isLoading } = useInventoryRuns();
  const activeRuns = runs.filter((r) => r.status !== 'COMPLETED' && r.status !== 'CANCELLED');
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [input, setInput] = useState('');
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const seq = useRef(1);
  const scan = useScanAsset(selectedRunId);
  const completeRun = useCompleteRun();

  useEffect(() => {
    if (!selectedRunId && activeRuns.length > 0) {
      setSelectedRunId(activeRuns[0]?.id ?? null);
    }
  }, [activeRuns, selectedRunId]);

  useEffect(() => {
    if (selectedRunId) inputRef.current?.focus();
  }, [selectedRunId]);

  const run: InventoryRun | undefined = runs.find((r) => r.id === selectedRunId);
  const showCamera = isNativeScanAvailable();

  async function onCamera() {
    try {
      const code = await scanOnce();
      if (code) {
        setInput(code);
        // Auto-submit via native scan — no reason to make the worker tap twice.
        setTimeout(() => {
          document.querySelector<HTMLFormElement>('.fa-scan-form')?.requestSubmit();
        }, 0);
      }
    } catch {
      toast.error(t('fa_inv.camera_error'));
    }
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    const code = input.trim();
    if (!code || !selectedRunId) return;
    scan.mutate(code, {
      onSuccess: (result) => {
        setHistory((h) => [
          { ...result, id: seq.current++, at: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }) },
          ...h,
        ].slice(0, 50));
        const key =
          result.status === 'COUNTED' ? 'fa_inv.toast_counted' :
          result.status === 'SURPLUS' ? 'fa_inv.toast_surplus' :
          'fa_inv.toast_duplicate';
        toast.success(t(key), { description: result.assetName ?? result.assetNumber });
        if (navigator.vibrate) navigator.vibrate(40);
      },
      onError: (err) => {
        const msg =
          (err as unknown as { response?: { data?: { message?: string | string[] } } })
            .response?.data?.message;
        toast.error(Array.isArray(msg) ? msg.join(', ') : (msg ?? t('fa_inv.toast_error')));
        if (navigator.vibrate) navigator.vibrate([40, 40, 40]);
      },
      onSettled: () => {
        setInput('');
        inputRef.current?.focus();
      },
    });
  }

  if (isLoading) return <div className="fa-page" aria-busy="true" />;

  if (activeRuns.length === 0) {
    return (
      <div className="fa-page">
        <div className="fa-empty">
          <span className="material-symbols-outlined">inventory_2</span>
          <div className="fa-empty-title">{t('fa_inv.no_runs_title')}</div>
          <div className="fa-empty-body">{t('fa_inv.no_runs_body')}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="fa-page">
      {/* Run selector */}
      {activeRuns.length > 1 && (
        <div className="fa-run-picker">
          {activeRuns.map((r) => (
            <button
              key={r.id}
              type="button"
              className={`fa-run-btn${r.id === selectedRunId ? ' active' : ''}`}
              onClick={() => setSelectedRunId(r.id)}
            >
              <span className="fa-run-num">{r.inventoryNumber}</span>
              <span className="fa-run-date">
                {new Date(r.inventoryDate).toLocaleDateString([], { month: 'short', day: 'numeric' })}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Active run summary */}
      {run && (
        <div className="fa-run-head">
          <div className="fa-run-num-big">{run.inventoryNumber}</div>
          <div className="fa-run-meta">
            <span className="fa-run-status" data-status={run.status}>
              {t(`fa_inv.status_${run.status.toLowerCase()}`)}
            </span>
            {run.assignee && <span className="fa-run-assignee">@{run.assignee}</span>}
          </div>
          <div className="fa-run-counts">
            <div className="fa-count-pill" data-kind="matched">
              <span>{run.matchedCount}</span>
              <small>{t('fa_inv.count_matched')}</small>
            </div>
            <div className="fa-count-pill" data-kind="unmatched">
              <span>{run.unmatchedCount}</span>
              <small>{t('fa_inv.count_unmatched')}</small>
            </div>
            <div className="fa-count-pill" data-kind="surplus">
              <span>{run.surplusCount}</span>
              <small>{t('fa_inv.count_surplus')}</small>
            </div>
          </div>
        </div>
      )}

      {/* Scan input */}
      <form className="fa-scan-form" onSubmit={onSubmit}>
        <span className="material-symbols-outlined fa-scan-icon">qr_code_scanner</span>
        <input
          ref={inputRef}
          className="fa-scan-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={t('fa_inv.scan_placeholder')}
          autoComplete="off"
          autoCapitalize="off"
          autoCorrect="off"
          disabled={scan.isPending || !selectedRunId}
          inputMode="text"
        />
        {showCamera && (
          <button
            type="button"
            className="fa-camera-btn"
            onClick={onCamera}
            disabled={scan.isPending || !selectedRunId}
            aria-label={t('fa_inv.camera_open')}
          >
            <span className="material-symbols-outlined">photo_camera</span>
          </button>
        )}
        <button
          type="submit"
          className="fa-scan-btn"
          disabled={!input.trim() || scan.isPending || !selectedRunId}
        >
          {scan.isPending ? '…' : t('fa_inv.scan_go')}
        </button>
      </form>

      {/* Complete run — visible once run has any items */}
      {run && (run.matchedCount + run.surplusCount) > 0 && run.status !== 'COMPLETED' && (
        <button
          type="button"
          className="fa-complete-btn"
          disabled={completeRun.isPending}
          onClick={() => {
            if (!selectedRunId) return;
            if (!window.confirm(t('fa_inv.confirm_complete'))) return;
            completeRun.mutate(selectedRunId, {
              onSuccess: () => toast.success(t('fa_inv.toast_completed')),
              onError: () => toast.error(t('fa_inv.toast_complete_fail')),
            });
          }}
        >
          <span className="material-symbols-outlined">task_alt</span>
          {completeRun.isPending ? t('fa_inv.completing') : t('fa_inv.complete_run')}
        </button>
      )}

      {/* Recent scans */}
      {history.length > 0 && (
        <div className="fa-history">
          <div className="fa-history-title">{t('fa_inv.recent')}</div>
          <div className="fa-history-list">
            {history.map((h) => (
              <div key={h.id} className="fa-history-row" data-status={h.status}>
                <div className="fa-history-icon">
                  <span className="material-symbols-outlined">
                    {h.status === 'COUNTED' ? 'check_circle' :
                     h.status === 'SURPLUS' ? 'add_circle' : 'error'}
                  </span>
                </div>
                <div className="fa-history-info">
                  <div className="fa-history-num">{h.assetNumber}</div>
                  {h.assetName && <div className="fa-history-name">{h.assetName}</div>}
                </div>
                <div className="fa-history-meta">
                  <div className="fa-history-status">
                    {t(`fa_inv.status_scan_${h.status.toLowerCase()}`)}
                  </div>
                  <div className="fa-history-time">{h.at}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
